import type {PublicScheduleEvent} from '../../../services/public-schedule.service'

/** What one entry to an Occurrence costs a visitor, as the schedule and its details show it. */
export type PublicEventPrice =
    | {kind: 'passes'}
    | {kind: 'free'}
    /**
     * `lowestPrice30dMinor` is set only for a reduction everyone gets, which is shown with it (ADR 0015).
     * `firstEntryMinor` is the lower price of a customer's first entry, offered beside the price, not as a reduction of it.
     */
    | {kind: 'paid'; amountMinor: number; currency: string; lowestPrice30dMinor: number | null; firstEntryMinor: number | null}

export function publicEventPrice(event: PublicScheduleEvent): PublicEventPrice | null {
    const price = entryPrice(event)
    if (price?.kind !== 'paid') return price
    const firstEntry = event.newCustomerPromotion?.finalAmountMinor
    return {...price, firstEntryMinor: typeof firstEntry === 'number' && firstEntry < price.amountMinor ? firstEntry : null}
}

function entryPrice(event: PublicScheduleEvent): PublicEventPrice | null {
    if (event.entryMode === 'PASSES') return {kind: 'passes'}
    if (event.entryMode === 'FREE' || event.priceMinor === 0) return {kind: 'free'}
    if (typeof event.priceMinor !== 'number') return null
    // What a guest pays, as the booking chooser shows it; a price without a reference is not a reduction.
    const promoted = event.guestCheckoutPrice
    if (!promoted) {
        // No guest quote (full, closed, no guest payments): the promotion everyone gets, never the bare base price.
        const automatic = event.automaticPromotion
        if (!automatic) return {kind: 'paid', amountMinor: event.priceMinor, currency: 'PLN', lowestPrice30dMinor: null, firstEntryMinor: null}
        const reference = automatic.lowestPrice30dMinor
        return {
            kind: 'paid',
            amountMinor: automatic.finalAmountMinor,
            currency: event.priceCurrency ?? 'PLN',
            lowestPrice30dMinor: typeof reference === 'number' && automatic.finalAmountMinor < reference ? reference : null,
            firstEntryMinor: null,
        }
    }
    const reduced = typeof promoted.lowestPrice30dMinor === 'number' && promoted.amountMinor < promoted.lowestPrice30dMinor
    return {kind: 'paid', amountMinor: promoted.amountMinor, currency: promoted.currency, lowestPrice30dMinor: reduced ? promoted.lowestPrice30dMinor ?? null : null, firstEntryMinor: null}
}

/** A reduction in whole percent, measured against its 30-day reference as the reduction itself is (ADR 0015). */
export function reductionPercent(amountMinor: number, referenceMinor: number): number {
    return Math.round((1 - amountMinor / referenceMinor) * 100)
}
